import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from './ui/input-otp';
import { toast } from 'sonner';

export default function VerifyPage({ onVerify }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    // Simulate initial page load
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));

    if (code === '123456') {
      toast.success('Verification successful');
      onVerify();
    } else {
      toast.error('Invalid code. Try 123456');
    }
    setLoading(false);
  };

  const handleResend = () => {
    toast.info('A new verification code was sent (demo: 123456)');
  };

  if (pageLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-3 text-[13px]">
        <div className="w-full max-w-sm transform scale-[0.95]">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-3">
              {/* Title Skeleton */}
              <div className="flex justify-center mb-2">
                <div className="h-5 w-40 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-md animate-shimmer bg-[length:200%_100%]" />
              </div>
              {/* Description Skeleton */}
              <div className="flex justify-center">
                <div className="h-3 w-56 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-md animate-shimmer bg-[length:200%_100%]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Label Skeleton */}
                <div className="h-3 w-24 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-md animate-shimmer bg-[length:200%_100%]" />
                
                {/* OTP Boxes Skeleton */}
                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="h-7 w-7 bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 rounded-md animate-bubble bg-[length:200%_100%]"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>

                {/* Button Skeleton */}
                <div className="h-8 w-full bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-md animate-shimmer bg-[length:200%_100%]" />

                {/* Resend Link Skeleton */}
                <div className="flex justify-center">
                  <div className="h-2.5 w-20 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-md animate-shimmer bg-[length:200%_100%]" />
                </div>

                {/* Tip Skeleton */}
                <div className="flex justify-center">
                  <div className="h-2 w-36 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-md animate-shimmer bg-[length:200%_100%]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add custom animations via style tag */}
        <style jsx>{`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }

          @keyframes bubble {
            0%, 100% {
              transform: scale(0.95);
              opacity: 0.8;
            }
            50% {
              transform: scale(1.05);
              opacity: 1;
            }
          }

          .animate-shimmer {
            animation: shimmer 2s ease-in-out infinite;
          }

          .animate-bubble {
            animation: bubble 1.5s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-3 text-[13px] animate-fadeIn">
      <div className="w-full max-w-sm transform scale-[0.95]">
        <Card className="border-0 shadow-xl animate-scaleIn">
          <CardHeader className="text-center pb-3">
            <CardTitle className="text-base font-semibold">Verify your account</CardTitle>
            <CardDescription className="text-[11px] text-slate-600">
              Enter the 6-digit code we sent to your email/phone
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label className="text-[11px]">Verification code</Label>
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={setCode}
                  containerClassName="justify-center gap-1"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="h-7 w-7 text-[11px]" />
                    <InputOTPSlot index={1} className="h-7 w-7 text-[11px]" />
                    <InputOTPSlot index={2} className="h-7 w-7 text-[11px]" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} className="h-7 w-7 text-[11px]" />
                    <InputOTPSlot index={4} className="h-7 w-7 text-[11px]" />
                    <InputOTPSlot index={5} className="h-7 w-7 text-[11px]" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full h-8 text-xs relative overflow-hidden"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <span className="inline-flex gap-0.5">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-bubbleLoader" style={{ animationDelay: '0s' }} />
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-bubbleLoader" style={{ animationDelay: '0.2s' }} />
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-bubbleLoader" style={{ animationDelay: '0.4s' }} />
                    </span>
                    <span>Verifying</span>
                  </span>
                ) : (
                  'Verify'
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-[11px] text-blue-600 hover:underline"
                >
                  Resend code
                </button>
              </div>

              <p className="text-[10px] text-center text-slate-500">
                Tip: For demo, use <span className="font-semibold">123456</span>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes bubbleLoader {
          0%, 60%, 100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          30% {
            transform: translateY(-8px) scale(1.2);
            opacity: 0.8;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-bubbleLoader {
          animation: bubbleLoader 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
